import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import GD from '../GD'
import { Grid } from '../components/Grid'
import { Icon } from '../components/Icon'
import { Close } from '../components/Icons'
import { IconButton } from '../components/IconButton'

const Root = styled.div<{mobile: boolean}>`
  position: relative;
  display: flex;
  padding: 17px 26px;
  width: 100%;
  color: ${props => props.theme.color.header.title};
  background: ${props => props.theme.color.primary};
  font-size: 22px;
  letter-spacing: 0.02em;
  cursor: ${props => !props.mobile && 'pointer'};
`
const ChatHeadTitle = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: bold;
  
  &::before {
    content: "";
    display: inline-block;
    top: calc(50% - 6px);
    border-radius: 50%;
    width: 6px;
    height: 6px;
    margin: 0 5px 0 0;
  }
  &[data-status="0"]::before {
    background-color: rgba(0, 0, 0, 0.2);
  }
  &[data-status="1"]::before {
    background-color: #ff9009e1;
  }
  &[data-status="2"]::before {
    background-color: #15ff09e0;
  }
`

interface Props {
  mobile: boolean
  noClose: boolean
  onDoubleClick: (e: React.MouseEvent) => void
}

const Header = ({ mobile, noClose, onDoubleClick }: Props) => {
  const [chatVO, setChatVO] = useState<ChatVO | null>(null)
  const [title, updateTitle] = useState<string>(chatVO?.title || '')
  const [wsStatus, setWSStatus] = useState<number>(0)

  const isOwnerRef = useRef<boolean>()

  const isGroupChat: boolean = chatVO?.users === undefined
    ? false
    : chatVO?.users.length > 1

  const checkIfOwner = useCallback(async () => {
    if(!chatVO) return false

    const user = await GD.GET_PROFILE.request()
    return user && chatVO?.ownerID === user.uid
  },[chatVO])

  useEffect(() => {
    if(!chatVO) return

    checkIfOwner().then((isOwner) => {
      isOwnerRef.current = isOwner
    })

  }, [chatVO, checkIfOwner])

  useEffect(() => {
    const handleChatOpened = (cvo: ChatVO) => {
      setChatVO(cvo)
      updateTitle(cvo.title)
    }
    GD.SET_CHAT.subscribe(handleChatOpened, 'ChatHeadCVO')
    return () => {
      GD.SET_CHAT.unsubscribe('ChatHeadCVO')
    }
  }, [])

  useEffect(() => {
    const handleWSAuthorized = (userType: string | undefined | null) => {
      setWSStatus(userType === 'guest' ? 1 : 2)
    }
    const handleWSClosed = () => {
      setWSStatus(0)
    }

    GD.WS_AUTHORIZED.subscribe(handleWSAuthorized, 'ChatHead')
    GD.WS_CLOSED.subscribe(handleWSClosed, 'ChatHead')
    return () => {
      GD.WS_AUTHORIZED.unsubscribe('ChatHead')
      GD.WS_CLOSED.unsubscribe('ChatHead')
    }
  }, [chatVO])

  if (!chatVO) return null

  return (
    <Root id='chatHead' mobile={mobile} onDoubleClick={onDoubleClick}>
      <Grid style={{ overflow: 'hidden' }} align="center" gap={10} fullwidth>
        <ChatHeadTitle data-show-status={!isGroupChat} data-status={wsStatus}>
          {title}
        </ChatHeadTitle>
      </Grid>
      {!noClose && (
        <Grid align="center" gap={8}>
          <IconButton
            icon={<Icon icon={<Close />} color="secondary" />}
            size="l"
            color="secondary"
            style={{ position: 'relative', right: -12 }}
            onClick={() => GD.CLOSE_CHAT.invoke()}
          />
        </Grid>
      )}
    </Root>
  )
}

export default Header
